import { setFilterBarTestId as setDataTableFilterBarTestId } from "@bcc/components/data-table/data-table.utils";
import { setAddProductSelectionModalTestId } from "@bcc/components/products/add-product-selection-modal.utils";
import { setManualAddProductModalTestId } from "@bcc/components/products/manual-add-product-modal.utils";
import { setProductSyncModalTestId } from "@bcc/components/products/product-sync-modal/product-sync-modal.utils";
import { PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS } from "@bcc/components/products/product-select-form/product-select-form-row.utils";
import {
  setModalNavigationBackButtonTestId,
  setModalNavigationContinueButtonTestId,
} from "@bcc/components/products/shopify-import-modal/components/modal-navigation/modal-navigation.utils";
import { setIntensitySectionTestId } from "@bcc/components/shopify-import/intensity-section/intensity-section.utils";
import { setProductFormTestId } from "@bcc/components/shopify-import/product-form/product-form.utils";
import { generateTestId } from "@client-shared";
import { setShopifyImportModalTestId } from "@bcc/components/products/shopify-import-modal/shopify-import-modal.utils";
import { setProductsViewTestId } from "@bcc/components/views/products-view.utils";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { FEATURE_FLAG_NAMES } from "@shared/types";
import { setSidebarNavTourId, TOUR_IDS } from "../tour-ids";
import type { FeatureTour } from "../tour.types";
import { DATA_TABLE_ROW_SELECTOR, testIdPrefixSelector, testIdSelector, tourTargetSelector } from "../tour.utils";

/** Dialog steps skip quickly when the trigger is absent/disabled (SS roasters, no installation) */
const DIALOG_STEP_TIMEOUT_MS = 4000;

const PRODUCTS_TABLE = tourTargetSelector(TOUR_IDS.products.table);
const MAP_PRODUCTS_BUTTON = testIdSelector(setProductsViewTestId("map-products-button"));
const ADD_PRODUCT_BUTTON = testIdSelector(setProductsViewTestId("add-product-button"));
const SHOPIFY_IMPORT_MODAL = testIdSelector(setShopifyImportModalTestId("content"));

/** Closes the mapping dialog when it is still open */
const CANCEL_PRODUCT_SYNC = { clickTarget: testIdSelector(setProductSyncModalTestId("cancel-button")) };

/** Closes the import modal via its Radix close button */
const CLOSE_SHOPIFY_IMPORT = { clickTarget: `${SHOPIFY_IMPORT_MODAL} [data-slot="dialog-close"]` };

const IMPORT_ROW_CHECKBOX = testIdPrefixSelector(
  generateTestId(PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT, PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.CHECKBOX, "")
);
const IMPORT_ROW_CHECKBOX_CHECKED = `${IMPORT_ROW_CHECKBOX}[data-state="checked"]`;
const IMPORT_CONTINUE_BUTTON = testIdSelector(setModalNavigationContinueButtonTestId("default"));
const IMPORT_BACK_BUTTON = testIdSelector(setModalNavigationBackButtonTestId("default"));
const PRODUCT_FORM_PRESENT = testIdPrefixSelector(setProductFormTestId(""));
const ANY_DIALOG = '[role="dialog"]';

// Prepare actions repeat every poll tick, so each click is guarded to stay idempotent
const OPEN_IMPORT_CONFIGURE_STEP = [
  { clickTarget: ADD_PRODUCT_BUTTON, unlessPresent: ANY_DIALOG },
  { clickTarget: IMPORT_ROW_CHECKBOX, unlessPresent: IMPORT_ROW_CHECKBOX_CHECKED },
  { clickTarget: IMPORT_CONTINUE_BUTTON },
];

export const productsTour: FeatureTour = {
  id: "products",
  titleKey: "tour.features.products.title",
  order: 4,
  requiredModule: "products",
  requiredPage: "products.view",
  steps: [
    {
      id: "products.nav",
      route: ROUTE_PATHS.PRODUCTS,
      target: tourTargetSelector(setSidebarNavTourId("products")),
      titleKey: "tour.steps.products.nav.title",
      bodyKey: "tour.steps.products.nav.body",
      placement: "right",
    },
    {
      id: "products.search-filters",
      route: ROUTE_PATHS.PRODUCTS,
      target: tourTargetSelector(TOUR_IDS.products.searchFilters),
      // The filter chips live in the table's filter bar, a separate subtree
      // from the search box — spotlight both together
      extendTargets: [`${PRODUCTS_TABLE} ${testIdSelector(setDataTableFilterBarTestId())}`],
      titleKey: "tour.steps.products.searchFilters.title",
      bodyKey: "tour.steps.products.searchFilters.body",
      placement: "bottom",
    },
    {
      id: "products.table",
      showsSampleData: true,
      route: ROUTE_PATHS.PRODUCTS,
      target: PRODUCTS_TABLE,
      titleKey: "tour.steps.products.table.title",
      bodyKey: "tour.steps.products.table.body",
      placement: "top",
      requiredPage: "products.edit",
      emptyState: {
        detectTarget: `${PRODUCTS_TABLE} ${DATA_TABLE_ROW_SELECTOR}`,
        bodyKey: "tour.steps.products.table.emptyBody",
      },
    },
    {
      id: "products.map-products-button",
      route: ROUTE_PATHS.PRODUCTS,
      target: MAP_PRODUCTS_BUTTON,
      titleKey: "tour.steps.products.mapProductsButton.title",
      bodyKey: "tour.steps.products.mapProductsButton.body",
      placement: "bottom",
      requiredPage: "products.edit",
      // Closes the mapping dialog when the user steps Back out of it
      prepare: [CANCEL_PRODUCT_SYNC],
      // SS roasters don't get this button at all — skip instantly instead of waiting out the timeout
      skipUnless: MAP_PRODUCTS_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.map-products",
      showsSampleData: true,
      route: ROUTE_PATHS.PRODUCTS,
      target: testIdSelector(setProductSyncModalTestId("content")),
      titleKey: "tour.steps.products.mapProducts.title",
      bodyKey: "tour.steps.products.mapProducts.body",
      placement: "right",
      requiredPage: "products.edit",
      prepare: [{ clickTarget: MAP_PRODUCTS_BUTTON }],
      // The dialog can only open from its button (absent for SS roasters / no installation)
      skipUnless: MAP_PRODUCTS_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product-button",
      route: ROUTE_PATHS.PRODUCTS,
      target: ADD_PRODUCT_BUTTON,
      titleKey: "tour.steps.products.addProductButton.title",
      bodyKey: "tour.steps.products.addProductButton.body",
      placement: "bottom",
      requiredPage: "products.create",
      // Closes whichever dialog is still open: the mapping dialog when moving
      // forward from map-products, the import modal when stepping Back
      prepare: [CANCEL_PRODUCT_SYNC, CLOSE_SHOPIFY_IMPORT],
      // SS roasters don't get this button at all — skip instantly instead of waiting out the timeout
      skipUnless: ADD_PRODUCT_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product",
      showsSampleData: true,
      route: ROUTE_PATHS.PRODUCTS,
      // Whichever add-product dialog the vendor's setup opens
      target: [
        SHOPIFY_IMPORT_MODAL,
        testIdSelector(setAddProductSelectionModalTestId("content")),
        testIdSelector(setManualAddProductModalTestId("content")),
      ].join(", "),
      titleKey: "tour.steps.products.addProduct.title",
      bodyKey: "tour.steps.products.addProduct.body",
      placement: "right",
      requiredPage: "products.create",
      prepare: [CANCEL_PRODUCT_SYNC, { clickTarget: IMPORT_BACK_BUTTON }, { clickTarget: ADD_PRODUCT_BUTTON }],
      skipUnless: ADD_PRODUCT_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product-badges",
      sinceVersion: 2,
      route: ROUTE_PATHS.PRODUCTS,
      target: testIdSelector(setProductFormTestId("badges")),
      titleKey: "tour.steps.products.addProductBadges.title",
      bodyKey: "tour.steps.products.addProductBadges.body",
      placement: "bottom",
      requiredPage: "products.create",
      requiredFeatureFlag: FEATURE_FLAG_NAMES.badges,
      prepare: OPEN_IMPORT_CONFIGURE_STEP,
      skipUnless: ADD_PRODUCT_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product-character",
      sinceVersion: 2,
      route: ROUTE_PATHS.PRODUCTS,
      target: testIdSelector(setProductFormTestId("character")),
      titleKey: "tour.steps.products.addProductCharacter.title",
      bodyKey: "tour.steps.products.addProductCharacter.body",
      placement: "bottom",
      requiredPage: "products.create",
      requiredFeatureFlag: FEATURE_FLAG_NAMES.badges,
      prepare: OPEN_IMPORT_CONFIGURE_STEP,
      skipUnless: PRODUCT_FORM_PRESENT,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product-roast-color",
      sinceVersion: 2,
      route: ROUTE_PATHS.PRODUCTS,
      target: testIdSelector(setIntensitySectionTestId("most-similar-to")),
      titleKey: "tour.steps.products.addProductRoastColor.title",
      bodyKey: "tour.steps.products.addProductRoastColor.body",
      placement: "top",
      requiredPage: "products.create",
      requiredFeatureFlag: FEATURE_FLAG_NAMES.intensityScore,
      prepare: OPEN_IMPORT_CONFIGURE_STEP,
      skipUnless: PRODUCT_FORM_PRESENT,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product-roast-scale",
      sinceVersion: 2,
      route: ROUTE_PATHS.PRODUCTS,
      target: testIdSelector(setIntensitySectionTestId("scale")),
      extendTargets: [testIdSelector(setIntensitySectionTestId("selected-readout"))],
      titleKey: "tour.steps.products.addProductRoastScale.title",
      bodyKey: "tour.steps.products.addProductRoastScale.body",
      placement: "top",
      requiredPage: "products.create",
      requiredFeatureFlag: FEATURE_FLAG_NAMES.intensityScore,
      prepare: OPEN_IMPORT_CONFIGURE_STEP,
      skipUnless: PRODUCT_FORM_PRESENT,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
  ],
};
